import { Divider, List, ListItemText as MUIListItemText } from '@mui/material';
import { ListItemTextProps as MUIListItemTextProps } from '@mui/material/ListItemText';
import { alpha } from '@mui/material/styles';
import styled, { css } from 'styled-components';

interface ExpandableProps {
  $expanded: boolean;
}

const ListHeaderText = styled(MUIListItemText)`
  && {
    & .MuiListItemText-primary {
      color: ${({ theme }) => theme.palette.secondary.main};
    }
  }
`;

const RailDivider = styled(Divider)`
  && {
    margin: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
    border-color: ${({ theme }) => theme.palette.divider};
  }
`;

type ListItemTextProps = MUIListItemTextProps & ExpandableProps;

const ListItemText = styled(MUIListItemText)<ListItemTextProps>`
  && {
    overflow: hidden;
    white-space: nowrap;
    margin: 4px 0;
    /* Keep the label in the layout and let it shrink below its content width so it
       is clipped from the right as the drawer narrows, rather than collapsing its
       box (which would shove the icon around). It also fades over the same duration
       as the width, so the close reads as one gentle motion. */
    flex-grow: 1;
    min-width: 0;
    transition: ${({ theme }) =>
      theme.transitions.create('opacity', {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      })};
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  }
`;

// A navigation list's rows, shared with anything that reads as one of them. The
// icon lives in a fixed-width column the width of the collapsed rail, so it
// never moves: collapsing narrows the drawer, clipping and fading the label
// while the icon and the active highlight stay put.
const menuRowStyles = css`
  & .MuiListItemButton-root {
    margin: ${({ theme }) => theme.spacing(1)} 0;
    /* Fixed 48px row height: in the collapsed rail this makes each item a 48px
       square (the column is 48px wide), identical to the toggle button. */
    min-height: ${({ theme }) => theme.spacing(6)};
    padding: ${({ theme }) => theme.spacing(1)} 0;
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    justify-content: flex-start;
    overflow: hidden;
  }
  & .MuiListItemIcon-root {
    min-width: ${({ theme }) => theme.spacing(6)};
    justify-content: center;
  }
  /* Selected item: a light tint of primary for the surface with a deep shade of
     primary for the label + icon. Both are derived from the brand colour, so the
     pairing stays legible in light and dark mode (primary.dark flips per mode). */
  & .Mui-selected {
    background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.14)};
    &:hover {
      background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.2)};
    }
    & .MuiListItemIcon-root,
    & .MuiListItemText-primary {
      color: ${({ theme }) => theme.palette.primary.dark};
    }
  }
`;

const StyledList = styled(List)`
  && {
    margin: 0 ${({ theme }) => theme.spacing(1)};
    /* Drop MUI List's default vertical padding. It otherwise stacks on top of the
       item margins at every group boundary, making the space above a heading/rail
       divider far larger than the space below it. With it gone, the symmetric item
       and divider margins separate the groups evenly in both states. */
    padding-top: 0;
    padding-bottom: 0;
    ${menuRowStyles}
  }
`;

export { ListHeaderText, ListItemText, menuRowStyles, RailDivider, StyledList };
